import { blue } from "@ant-design/colors"
import { Alert, Button, Input, Space, Tabs, Tour, Typography } from "antd"
import { CSSProperties, useContext, useEffect, useState } from "react"
import { faLeft } from '@fortawesome/pro-solid-svg-icons'
import BudgetCalculatorContext from "./BudgetCalculatorContext"
import { formatDollars, formatPercentage } from "./budgetCalculatorModel"
import Equipment from "./Equipment"
import Labor from "./Labor"
import Materials from "./Materials"
import Overhead from "./Overhead"
import Sales from "./Sales"
import SaveIndicator from "./SaveIndicator"
import Subcontractors from "./Subcontractors"
import NewOverview from "./NewOverview"
import ApplyWorksheetModal from './ApplyWorksheetModal'
import IconAndTextButton from "../IconAndTextButton"

const { Text } = Typography;

const tabHeader = (title: string, value: number, activeKey: string, percentage: boolean = false) => {
  const style: CSSProperties = {}
  if (activeKey === title.toLowerCase()) {
    style.fontSize = '14px'
    style.color = blue.primary
  }
  return <>
    <span style={{ fontSize: '16px' }}>{title}</span>
    <br />
    <Text type="secondary" style={style}>{percentage ? formatPercentage(value) : formatDollars(value)}</Text>
  </>
}

type CalculatorProps = {
  initialTab?: string
  goBackToList?: () => void
}

const Calculator = ({ initialTab, goBackToList }: CalculatorProps) => {
  const { dispatch, summary, statistics, saveState, worksheet, showCongratulations } = useContext(BudgetCalculatorContext)
  const [activeKey, setActiveKey] = useState(initialTab || 'labor')
  const [isTourOpen, setIsTourOpen] = useState(false)
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false)
  const TOUR_VIEWED_KEY = 'budget-calculator-tour-viewed'

  useEffect(() => {
    const tourAlreadyViewed = localStorage.getItem(TOUR_VIEWED_KEY) === 'true'
    const congratsDismissed = !showCongratulations

    console.log('Tour Effect - showCongratulations:', showCongratulations, 'congratsDismissed:', congratsDismissed, 'tourAlreadyViewed:', tourAlreadyViewed, 'isTourOpen:', isTourOpen)

    if (congratsDismissed && !tourAlreadyViewed) {
      setTimeout(() => {
        console.log('Tour: Setting isTourOpen to true')
        setIsTourOpen(true)
      }, 500)
    }
  }, [showCongratulations])

  // Map tour steps to their corresponding tab keys
  const tourStepToTab = ['labor', 'materials', 'equipment', 'subcontractor', 'overhead', 'newoverview', 'newoverview', 'newoverview']

  const tourSteps = [
    {
      title: 'Labor',
      description: 'Review and adjust your crew\'s wages, hours worked per year, unbillable hours, and overtime hours. See the full cost of your team.',
      target: () => document.querySelector('.ant-tabs-tab[data-node-key="labor"]') as HTMLElement,
    },
    {
      title: 'Materials',
      description: 'Manage the total projected amount your business will spend on materials in a year. Include all materials you purchase from your vendors specifically for jobs.',
      target: () => document.querySelector('.ant-tabs-tab[data-node-key="materials"]') as HTMLElement,
    },
    {
      title: 'Equipment',
      description: 'Manage your equipment\'s costs and years of ownership or use. Include all expensive tools, equipment, trucks, & trailers, both owned outright and leased or financed.',
      target: () => document.querySelector('.ant-tabs-tab[data-node-key="equipment"]') as HTMLElement,
    },
    {
      title: 'Subcontractor',
      description: 'Manage your total anticipated subcontractor costs in a season. If you don\'t use subcontractors, skip this tab.',
      target: () => document.querySelector('.ant-tabs-tab[data-node-key="subcontractor"]') as HTMLElement,
    },
    {
      title: 'Overhead',
      description: 'Manage all of your overhead costs. This will include things like advertising, fuel, insurance, rent, utilities, etc. Ensure every overhead expense your business has is entered here.',
      target: () => document.querySelector('.ant-tabs-tab[data-node-key="overhead"]') as HTMLElement,
    },
    {
      title: 'Overview',
      description: 'Get a complete look over your business\'s costs and profit margins.',
      target: () => document.querySelector('.ant-tabs-tab[data-node-key="newoverview"]') as HTMLElement,
    },
    {
      title: 'Job & Profit Widget',
      description: 'Enter labor hours and material costs for a recent job to see the total needed to cover expenses and reach your profit goal.',
      target: () => {
        const elements = Array.from(document.querySelectorAll('div'))
        return elements.find(el =>
          el.style.backgroundColor === 'rgb(245, 245, 245)' &&
          el.style.padding === '40px'
        ) as HTMLElement || null
      },
    },
    {
      title: 'Apply to Budget',
      description: 'Use this button to apply your budget and activate these markups and margins to your estimating calculations. You are now ready to build a profitably priced estimate based on your numbers!',
      target: () => {
        const buttons = Array.from(document.querySelectorAll('button'))
        return buttons.find(btn => btn.textContent?.includes('Apply to Budget')) as HTMLElement || null
      },
    }
  ]

  const tabs = [
    { label: tabHeader('Labor', summary.labor, activeKey), key: "labor", children: <Labor /> },
    { label: tabHeader('Materials', summary.materials, activeKey), key: "materials", children: <Materials /> },
    { label: tabHeader('Equipment', summary.equipment, activeKey), key: "equipment", children: <Equipment /> },
    { label: tabHeader('Subcontractor', summary.subcontractors, activeKey), key: "subcontractor", children: <Subcontractors /> },
    { label: tabHeader('Overhead', statistics.totalOverhead, activeKey), key: "overhead", children: <Overhead /> },
    { label: tabHeader('Overview', statistics.netProfitMargin, activeKey, true), key: "newoverview", children: <NewOverview /> },
    { label: tabHeader('Sales', statistics.salesGoal, activeKey), key: "sales", children: <Sales /> },
  ]

  return <Space direction="vertical" className="budget-calculator" style={{ width: '100%' }}>
    { worksheet.isApplied &&
      <Alert type="warning" message="Changes to this budget worksheet will not be saved because it is currently applied to a budget year.  Please copy this worksheet to make a new version of this budget worksheet if you want to make saved changes." />
    }

    {/* Back to List, Worksheet Name, and Apply to Budget Button Row */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
      {goBackToList && <IconAndTextButton onClick={goBackToList} icon={faLeft} text="Back to List"/>}
      <div style={{ flex: 1 }} />
      <Input
        placeholder="Budget worksheet name"
        value={worksheet.title}
        style={{
          flex: '0 1 600px',
          fontSize: '16px',
          padding: '8px 16px',
          height: '48px',
          borderRadius: '8px',
          textAlign: 'center',
          border: '1px solid #d9d9d9'
        }}
        onChange={e => dispatch({ type: 'TITLE_CHANGED', payload: e.target.value })}
        disabled={worksheet.isApplied}
      />
      <div style={{ flex: 1 }} />
      <Button
        disabled={worksheet.isApplied}
        onClick={() => setIsApplyModalOpen(true)}
        style={{
          backgroundColor: worksheet.isApplied ? undefined : '#ff6b4a',
          borderColor: worksheet.isApplied ? undefined : '#ff6b4a',
          color: 'white',
          height: '48px',
          padding: '0 24px',
          fontSize: '16px',
          borderRadius: '8px',
          fontWeight: 500
        }}
      >
        Apply to Budget
      </Button>
    </div>

    <ApplyWorksheetModal open={isApplyModalOpen} onCancel={() => setIsApplyModalOpen(false)} />

    <Tabs
      id="budget-calculator-tabs"
      activeKey={activeKey}
      onChange={(x => setActiveKey(x))}
      tabBarExtraContent={<Space size="large"><SaveIndicator saveState={saveState} /></Space>}
      items={tabs}
    />

    <Tour
      open={isTourOpen}
      onChange={(current) => setActiveKey(tourStepToTab[current])}
      onClose={() => {
        console.log('Tour: onClose called')
        localStorage.setItem(TOUR_VIEWED_KEY, 'true')
        setIsTourOpen(false)
      }}
      onFinish={() => {
        console.log('Tour: onFinish called')
        localStorage.setItem(TOUR_VIEWED_KEY, 'true')
        setIsTourOpen(false)
      }}
      steps={tourSteps}
    />
  </Space>
}
export default Calculator
